import { useState } from 'react';
import { ActionModalContent } from 'components/ProfileUIComponents';
import { IOrgDetails } from 'typings/organization';
import { Flexbox, Text } from '@morressier/ts';
import {
  faSearch,
  faChevronRight,
  faChevronLeft
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import { getQuery } from 'utils/query';
import {
  Container,
  StyledRadio,
  Department,
  MediumInput,
  ModalForm
} from '../style';
import { VIEWS } from '../EmailAffiliation';
import { SecondaryText } from 'containers/Profile/styles';
import { getInstitutionTypeText } from 'utils/utils';
import { logEvent } from 'utils/eventTracking';

interface IOrganizationProps {
  email: string;
  orgs: IOrgDetails[];
  selectedOrganizationId: string;
  onUpdate: (ringgoldId: string) => void;
  handleNavigaiton: (nextView: VIEWS) => void;
  onDismiss: () => void;
}
interface FormValues {
  ringgoldId: string;
}

const OrganizationsView = (props: IOrganizationProps) => {
  const router = useRouter();
  const queryParams = getQuery(router);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>();

  const {
    email,
    orgs,
    selectedOrganizationId,
    onUpdate,
    handleNavigaiton,
    onDismiss
  } = props;

  const [filterOrgs, setFilterOrgs] = useState<IOrgDetails[]>(orgs);

  const onSubmit = async (data: FormValues) => {
    const { ringgoldId } = data;
    logEvent('PROFILE_ADD_AFFILIATION_ORGANIZATION_SELECT_CONTINUE_CLICKED', {
      organization_id: ringgoldId
    });
    onUpdate(ringgoldId);
    handleNavigaiton(VIEWS.DetailsView);
  };

  const onSecondaryClick = () => {
    logEvent(
      'PROFILE_ADD_AFFILIATION_ORGANIZATION_SELECT_USE_DIFFERENT_EMAIL_CLICKED'
    );
    handleNavigaiton(VIEWS.EmailView);
  };

  const filterOrganizations = (searchString: string) => {
    const filtered = orgs.filter((orgOption: IOrgDetails) => {
      if (
        orgOption.departmentName.toLowerCase().includes(searchString) ||
        orgOption.organizationName.toLowerCase().includes(searchString) ||
        orgOption.location.toLowerCase().includes(searchString)
      )
        return orgOption;
    });
    setFilterOrgs(filtered);
  };

  return (
    <ModalForm onSubmit={handleSubmit(onSubmit)}>
      <ActionModalContent
        title="Add affiliation"
        primaryAction={{
          title: 'Continue',
          iconAfter: <FontAwesomeIcon icon={faChevronRight} />,
          type: 'submit'
        }}
        secondaryAction={{
          title: 'Update email',
          iconBefore: <FontAwesomeIcon icon={faChevronLeft} />,
          onClick: onSecondaryClick,
          type: 'button'
        }}
        cautionaryAction={{
          title: 'Exit without saving',
          onClick: () => {
            logEvent('PROFILE_AFFILIATION_EXIT_WITHOUT_SAVING_CLICKED', {
              step: 'ORGANIZATION_SELECT'
            });
            onDismiss();
          },
          type: 'button'
        }}
      >
        <Container>
          <Text size="h3_new" fontWeight="bold">
            Choose your organization{' '}
          </Text>
          <Text>
            {queryParams.flow === 'account-activation'
              ? 'Help the community find you and the research associated with your organization by linking your current primary affiliation to your Morressier profile.'
              : `Help the community find you and the research associated with your
            organization by linking your research affiliation to your Morressier
            profile.`}
          </Text>
        </Container>
        <Container>
          <Text>Showing matches for @{email.split('@')[1]}</Text>
          <MediumInput
            placeholder="Search by name"
            endIcon={<FontAwesomeIcon icon={faSearch} />}
            onChange={e => {
              filterOrganizations(e.target.value.toLowerCase());
            }}
          />
          <Text>{filterOrgs?.length} results</Text>
          {errors.ringgoldId && (
            <p style={{ color: 'red' }}>{errors.ringgoldId.message}</p>
          )}
        </Container>

        <Flexbox flexDirection="column" gap="1rem">
          {filterOrgs?.map(item => (
            <StyledRadio
              defaultChecked={
                !!(
                  selectedOrganizationId &&
                  item.ringgoldId == selectedOrganizationId
                )
              }
              key={item.ringgoldId}
              value={item.ringgoldId}
              {...register('ringgoldId', {
                required: 'Please select one organization',
                onChange: event => {
                  logEvent('PROFILE_ADD_AFFILIATION_ORGANIZATION_SELECT', {
                    organization_id: event?.target?.value
                  });
                }
              })}
            >
              <div>
                <Text fontWeight="bold">{item.departmentName}</Text>
                {item.organizationName !== item.departmentName ? (
                  <Department>{item.organizationName}</Department>
                ) : (
                  ''
                )}
                <SecondaryText>
                  {getInstitutionTypeText(item.organizationType)}
                </SecondaryText>
                <SecondaryText>{item.location}</SecondaryText>
              </div>
            </StyledRadio>
          ))}
        </Flexbox>
      </ActionModalContent>
    </ModalForm>
  );
};

export default OrganizationsView;
